import { useEffect, useState, useRef, useCallback } from "react";
import { Modal, Button } from "antd";
import { ExclamationCircleFilled } from "@ant-design/icons";
import { trace } from "../utils/debugTrace";

const STORAGE_KEY = "pos_single_instance_lock";
const EXEMPT_PATHS = ["/dualscreen"];
const HEARTBEAT_MS = 3000;
// A live tab (foreground, or merely backgrounded-but-not-frozen) keeps
// heartbeating on schedule. A tab Chrome has frozen/discarded, or that
// crashed without a chance to clean up, simply stops writing — so any other
// tab can safely reclaim the lock once it's gone quiet for a few missed
// beats. This is what actually fixes "stuck on POS Already Open forever":
// a frozen-but-undestroyed tab still legally owns a Web Lock and can't be
// forced to release it, so staleness (not lock ownership) has to be the
// source of truth.
// Matches the "frozen" threshold watchdog.js already uses (see startWatchdog)
// to distinguish a genuine freeze from ordinary background timer throttling —
// reusing that proven number instead of inventing a new one here.
const STALE_MS = 15000;
const CLAIM_JITTER_MS = 60;
// How long to wait for a pong before assuming nobody answered. This is a
// same-origin, same-machine BroadcastChannel round trip — a genuinely alive
// tab answers in low single-digit ms, so this only ever blocks the reload
// path for real (see requestLivenessPong).
const LIVENESS_PING_TIMEOUT_MS = 150;
const LIVENESS_CHANNEL_NAME = "pos_single_instance_liveness";

const makeInstanceId = () =>
  window.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;

// sessionStorage is scoped to this one tab and — unlike a fresh random id per
// mount — survives a hard refresh (Ctrl+R) of that same tab, while a
// genuinely different tab always starts with empty sessionStorage and gets
// its own id. That asymmetry is what lets claim() below tell "this is me,
// reloading" apart from "this is another tab" with certainty, instead of
// guessing from lock staleness.
const SESSION_ID_KEY = "pos_single_instance_session_id";

const getSessionInstanceId = () => {
  try {
    let id = sessionStorage.getItem(SESSION_ID_KEY);
    if (!id) {
      id = makeInstanceId();
      sessionStorage.setItem(SESSION_ID_KEY, id);
    }
    return id;
  } catch {
    return makeInstanceId();
  }
};

const readLock = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

// Last id seen on a heartbeat tick. Ticks fire every HEARTBEAT_MS forever, so
// tracing each one floods the 200-entry ring buffer (debugTrace.js) and evicts
// everything else within minutes — it made an unrelated reload-loop investigation
// undiagnosable. The zombie-heartbeat signal (28ea6849) is a tick arriving under
// an *unexpected* id, so only the id change carries information: trace the first
// tick of each id and drop the identical repeats.
let lastHeartbeatId = null;

const writeLock = (id, source) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ id, ts: Date.now() }));
    // Tagged per call site so a resurrection can be traced back to the exact
    // code path that wrote it, instead of only seeing the write's effect.
    if (source === "heartbeat:tick") {
      if (id === lastHeartbeatId) return;
      lastHeartbeatId = id;
    }
    trace("SingleInstanceGuard", "writeLock", { id, source });
  } catch {
    // Storage unavailable (private mode, quota) — caller falls back to active.
  }
};

const clearLock = (id) => {
  try {
    if (readLock()?.id === id) localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};

const isStale = (lock) => !lock || Date.now() - lock.ts > STALE_MS;

// Module-level (not component-scoped) record of the instance id this tab
// currently owns, if any. watchdog.js's freeze-recovery reload can't reach
// into the mounted component's refs, but it IS running JS at the moment it
// decides to reload (that's how it detected the freeze ended) — so it can
// call releaseInstanceLock() right before window.location.reload() to hand
// the lock off cleanly, instead of depending on pagehide firing in time on
// an event loop that's still catching up from the same stall that caused
// the freeze in the first place.
let ownedInstanceId = null;

// F-20: sessionStorage cloning on tab duplication means "same id" (the
// fast-path check in claim() below) is ambiguous between two cases that look
// identical from lock storage alone — this tab's own previous document dying
// mid-navigation (pagehide may not have run yet, see the comment at that
// check) versus a duplicated tab's own later reload/navigation, which
// inherited the same cloned id, potentially stealing the lock from a
// still-live original. Lock timestamp freshness can't tell these apart
// either — both look recently-heartbeated. A direct liveness ping over
// BroadcastChannel can:
// a tab that's mid-teardown is unreliable to hear from (same race pagehide
// already has), while a normal, undisturbed live tab reliably answers
// within a few ms — so silence within LIVENESS_CHANNEL_NAME is safe to
// treat as "nobody else is really here" and a pong means "don't reclaim."
let livenessChannel = null;
try {
  if (typeof BroadcastChannel !== "undefined") {
    livenessChannel = new BroadcastChannel(LIVENESS_CHANNEL_NAME);
    livenessChannel.onmessage = (e) => {
      if (e.data?.type === "ping" && e.data.id === ownedInstanceId) {
        livenessChannel.postMessage({ type: "pong", id: e.data.id, requestId: e.data.requestId });
      }
    };
  }
} catch {
  livenessChannel = null;
}

const requestLivenessPong = (id) =>
  new Promise((resolve) => {
    if (!livenessChannel) {
      resolve(false);
      return;
    }
    const requestId = makeInstanceId();
    let settled = false;
    const finish = (result) => {
      if (settled) return;
      settled = true;
      livenessChannel.removeEventListener("message", onMessage);
      clearTimeout(timer);
      resolve(result);
    };
    const onMessage = (e) => {
      if (e.data?.type === "pong" && e.data.id === id && e.data.requestId === requestId) finish(true);
    };
    livenessChannel.addEventListener("message", onMessage);
    const timer = setTimeout(() => finish(false), LIVENESS_PING_TIMEOUT_MS);
    try {
      livenessChannel.postMessage({ type: "ping", id, requestId });
    } catch {
      finish(false);
    }
  });

export function releaseInstanceLock() {
  if (!ownedInstanceId) return;
  const before = readLock();
  clearLock(ownedInstanceId);
  trace("SingleInstanceGuard", "releaseInstanceLock (external caller)", {
    id: ownedInstanceId,
    lockBefore: before,
    lockAfter: readLock(),
  });
  ownedInstanceId = null;
}

const SingleInstanceGuard = ({ children }) => {
  const isExempt = EXEMPT_PATHS.some((p) => window.location.pathname.startsWith(p));
  const [state, setState] = useState(isExempt ? "active" : "acquiring");
  const stateRef = useRef(state);
  stateRef.current = state;

  const instanceIdRef = useRef(getSessionInstanceId());
  const heartbeatTimerRef = useRef(null);
  const pollTimerRef = useRef(null);
  const blockedRecheckTimerRef = useRef(null);

  // While blocked, self-heal exactly when the lock we saw actually goes
  // stale, instead of waiting for the next up-to-3s poll tick. This matters
  // most right after a watchdog.js freeze-recovery reload, where the lock's
  // age can land just shy of STALE_MS at claim time — with this, that gap
  // closes in milliseconds instead of up to HEARTBEAT_MS.
  const scheduleBlockedRecheck = useCallback((lock) => {
    clearTimeout(blockedRecheckTimerRef.current);
    if (!lock) return;
    const msUntilStale = STALE_MS - (Date.now() - lock.ts) + 50; // small buffer past the boundary
    blockedRecheckTimerRef.current = setTimeout(() => {
      if (stateRef.current === "blocked") claim(false);
    }, Math.max(0, msUntilStale));
  }, []);

  const startHeartbeat = useCallback(() => {
    const id = instanceIdRef.current;
    writeLock(id, "startHeartbeat:init");
    ownedInstanceId = id;
    clearInterval(heartbeatTimerRef.current);
    heartbeatTimerRef.current = setInterval(() => {
      // Guard against a tick that was already queued by the timer engine
      // before clearInterval() ran (e.g. during onPageHide/unmount) — that
      // queued callback still fires, and clearInterval can't un-queue it.
      // ownedInstanceId is nulled out synchronously the moment this tab
      // releases the lock, so checking it here (rather than trusting
      // clearInterval alone) closes that gap even if the interval outlives
      // its own cancellation by one tick.
      if (ownedInstanceId !== id) return;
      const current = readLock();
      if (!current || current.id === id) {
        writeLock(id, "heartbeat:tick");
      } else {
        // Someone force-claimed the lock out from under us ("Use POS Here").
        clearInterval(heartbeatTimerRef.current);
        ownedInstanceId = null;
        setState("taken_over");
      }
    }, HEARTBEAT_MS);
  }, []);

  const claimFallback = useCallback(
    (id, existing, force) => {
      if (force) {
        trace("SingleInstanceGuard", "claim(force)", { id, existing, pathname: window.location.pathname });
        clearTimeout(blockedRecheckTimerRef.current);
        writeLock(id, "claim:force");
        setState("active");
        startHeartbeat();
        return;
      }

      if (!isStale(existing)) {
        trace("SingleInstanceGuard", "claim blocked (lock not stale)", {
          id,
          existing,
          ageMs: existing ? Date.now() - existing.ts : null,
          pathname: window.location.pathname,
        });
        setState("blocked");
        scheduleBlockedRecheck(existing);
        return;
      }

      trace("SingleInstanceGuard", "claim staking (free or stale)", { id, existing, pathname: window.location.pathname });
      // Free or stale — stake a claim, then confirm nobody else raced in
      // during a short jitter window before committing to "active".
      writeLock(id, "claim:stake");
      setTimeout(() => {
        const afterJitter = readLock();
        if (afterJitter?.id === id) {
          trace("SingleInstanceGuard", "claim confirmed → active", { id });
          clearTimeout(blockedRecheckTimerRef.current);
          setState("active");
          startHeartbeat();
        } else {
          trace("SingleInstanceGuard", "claim lost jitter race → blocked", { id, afterJitter });
          setState("blocked");
          scheduleBlockedRecheck(afterJitter);
        }
      }, Math.random() * CLAIM_JITTER_MS);
    },
    [startHeartbeat, scheduleBlockedRecheck]
  );

  const claim = useCallback(
    (force) => {
      const id = instanceIdRef.current;
      const existing = readLock();

      if (existing?.id === id) {
        // The lock on record was written by this very tab's id, in the life
        // it had before this mount — either a plain F5 reload, or the app's
        // own hard `window.location.assign/href` navigation to a *different*
        // URL (e.g. the lock screen's /pos → /sign-in → /pos relogin hop, or
        // any other in-app redirect). sessionStorage — and the instance id
        // derived from it — survives both identically, since it's scoped to
        // the tab, not the URL (BUG-2067/2068: this used to only run this
        // check when Navigation Timing reported "reload", so a cross-URL hard
        // navigation whose pagehide lost the race to clear the lock in time
        // fell straight into the generic "not stale → blocked" branch below,
        // showing "POS is already open in another tab" to the very tab that
        // owns it). sessionStorage is also cloned on tab duplication, so the
        // id alone doesn't rule out a duplicated tab's own later
        // reload/navigation reporting this same id (see the F-20 comment
        // above requestLivenessPong). Confirm nobody else is still alive
        // under this id before reclaiming outright; only fall back to the
        // normal force/staleness path below if someone answers.
        requestLivenessPong(id).then((stillAlive) => {
          if (stillAlive) {
            trace("SingleInstanceGuard", "claim deferred (still alive elsewhere)", { id, existing });
            claimFallback(id, existing, force);
            return;
          }
          // A hard reload or a same-tab hard navigation doesn't guarantee the
          // old document's pagehide fires and clears the lock before this new
          // document mounts (slow reload / service worker / a
          // backgrounded-tab timer throttle can all delay or skip it), so
          // waiting on staleness here — as the generic path does — can
          // wrongly show "POS Already Open" for up to STALE_MS. Nobody
          // answered the liveness ping, so reclaim outright instead.
          trace("SingleInstanceGuard", "claim reclaim (own id, no live owner)", { id, existing });
          writeLock(id, "claim:reclaim-own");
          setState("active");
          startHeartbeat();
        });
        return;
      }

      claimFallback(id, existing, force);
    },
    [startHeartbeat, claimFallback]
  );

  useEffect(() => {
    trace("SingleInstanceGuard", "mount", {
      id: instanceIdRef.current,
      pathname: window.location.pathname,
      isExempt,
      existing: readLock(),
    });
    if (isExempt) return;

    if (typeof Storage === "undefined") {
      setState("active");
      return;
    }

    claim(false);

    const recheckIfBlocked = () => {
      if (stateRef.current !== "blocked") return;
      if (isStale(readLock())) claim(false);
    };

    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) recheckIfBlocked();
    };
    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      if (stateRef.current === "active") {
        // stateRef isn't touched by onPageHide/unmount, so a visibilitychange
        // that lands after this tab already released the lock (confirmed via
        // trace: onVisibility:reassert firing 1ms after pagehide's clearLock)
        // would otherwise still see "active" here and write the lock straight
        // back. ownedInstanceId is nulled out synchronously on release, so
        // bail before touching storage if that's already happened.
        if (ownedInstanceId !== instanceIdRef.current) return;
        // Don't blindly rewrite — while we were backgrounded, our own
        // heartbeat interval was throttled too, so another tab may have
        // legitimately claimed the lock (staleness) without us noticing yet.
        // Re-check before reasserting ownership, otherwise we'd silently
        // stomp their claim and both tabs would believe they're active.
        const current = readLock();
        if (current && current.id !== instanceIdRef.current) {
          trace("SingleInstanceGuard", "visibility lost lock to another id → taken_over", { id: instanceIdRef.current, current });
          clearInterval(heartbeatTimerRef.current);
          ownedInstanceId = null;
          setState("taken_over");
        } else {
          writeLock(instanceIdRef.current, "onVisibility:reassert");
        }
      } else {
        recheckIfBlocked();
      }
    };
    const onPageHide = () => {
      // Stop the heartbeat BEFORE clearing the lock. Otherwise, if the
      // interval is still alive at the moment its next tick lands (pagehide
      // firing doesn't guarantee in-flight timers are torn down instantly),
      // its own `!current` fallback branch (see startHeartbeat) reads back
      // the just-cleared lock as "nobody holds it, so I still do" and
      // rewrites it under this dying tab's id — resurrecting a zombie lock
      // moments after this tab explicitly released it, blocking the next
      // tab's claim with a false "POS Already Open".
      clearInterval(heartbeatTimerRef.current);
      const before = readLock();
      clearLock(instanceIdRef.current);
      ownedInstanceId = null;
      trace("SingleInstanceGuard", "pagehide → clearLock", { id: instanceIdRef.current, lockBefore: before, lockAfter: readLock() });
    };

    window.addEventListener("storage", onStorage);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);

    // `storage` never fires in the tab that wrote the value, and delivery to
    // a frozen/backgrounded tab isn't guaranteed — poll as a safety net so a
    // blocked tab still self-heals even without an event ever landing.
    pollTimerRef.current = setInterval(recheckIfBlocked, HEARTBEAT_MS);

    return () => {
      window.removeEventListener("storage", onStorage);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
      clearInterval(pollTimerRef.current);
      clearInterval(heartbeatTimerRef.current);
      clearTimeout(blockedRecheckTimerRef.current);
      const before = readLock();
      clearLock(instanceIdRef.current);
      ownedInstanceId = null;
      trace("SingleInstanceGuard", "react unmount → clearLock", { id: instanceIdRef.current, lockBefore: before, lockAfter: readLock() });
    };
  }, [claim, isExempt]);

  const handleUseHere = () => claim(true);

  if (state === "active") return children;

  if (state === "acquiring") {
    return null;
  }

  const isTakenOver = state === "taken_over";

  return (
    <Modal
      open={true}
      centered
      closable={false}
      footer={null}
      maskClosable={false}
      width={420}
      styles={{ body: { padding: 32, borderRadius: 12 } }}
    >
      <div style={{ textAlign: "center" }}>
        <div
          style={{
            width: 52,
            height: 52,
            background: isTakenOver ? "#EFF6FF" : "#FFF4E5",
            borderRadius: "50%",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: 20,
          }}
        >
          <ExclamationCircleFilled style={{ color: isTakenOver ? "#2970FF" : "#FFA126", fontSize: 26 }} />
        </div>

        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
          {isTakenOver ? "POS Opened Elsewhere" : "POS Already Open"}
        </div>

        <div style={{ fontSize: 14, color: "#6B7280", marginBottom: 28, lineHeight: 1.6 }}>
          {isTakenOver
            ? "POS is now being used in another tab. This tab has been deactivated to prevent data conflicts."
            : "POS is already running in another tab. To protect your data and prevent conflicts, only one instance can be active at a time."}
        </div>

        <Button
          type="primary"
          size="large"
          block
          onClick={isTakenOver ? () => window.location.reload() : handleUseHere}
          style={{
            height: 44,
            borderRadius: 8,
            fontWeight: 500,
            fontSize: 15,
            background: "#2970FF",
          }}
        >
          {isTakenOver ? "Reload This Tab" : "Use POS Here"}
        </Button>
      </div>
    </Modal>
  );
};

export default SingleInstanceGuard;
